<!--
Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->

<!-- ONLY one element type is active at any given point. Mark all interactive elements with cdkFocusInitial
to correctly initialize focus on the first item of the page -->
@switch (data.elementType) {
    @case (trailElementType.Icon) {
        <div class="trail-container trail-icon-container">
            <mat-icon class="trail-icon font-color-primary" [svgIcon]="data.icon"></mat-icon>
        </div>
    }
    @case (trailElementType.Checkbox) {
        <div class="trail-container trail-checkbox-container">
            <mat-checkbox
                [disabled]="data.disabled"
                [(ngModel)]="data.toggleState"
                (change)="toggleClick($event)"
                [aria-label]="ariaLabel"
                cdkFocusInitial
            ></mat-checkbox>
        </div>
    }
    @case (trailElementType.Switch) {
        <div class="trail-container trail-switch-container">
            <mat-slide-toggle
                [disabled]="data.disabled"
                [(ngModel)]="data.toggleState"
                (change)="toggleClick($event)"
                [aria-label]="ariaLabel"
                cdkFocusInitial
            >
            </mat-slide-toggle>
        </div>
    }
    @case (trailElementType.IconButton) {
        <div class="trail-container trail-icon-button-container">
            <button mat-icon-button (click)="primaryButtonClick($event)" [attr.aria-label]="ariaLabel" cdkFocusInitial>
                <mat-icon [svgIcon]="data.icon"></mat-icon>
            </button>
        </div>
    }
    @case (trailElementType.TextButton) {
        <div class="trail-container trail-buttons-container">
            @if (data.primaryButton) {
                <button
                    mat-button
                    class="font-button2"
                    (click)="primaryButtonClick($event)"
                    [disabled]="data.primaryButton.isDisabled || data.disabled"
                    cdkFocusInitial
                    [attr.aria-label]="primaryButtonAriaLabel"
                >
                    @if (data.primaryButton.svgIcon) {
                        <mat-icon
                            class="trail-button-icon"
                            [class.trail-button-icon-hidden]="data.primaryButton.spinnerOverlay"
                            [svgIcon]="data.primaryButton.svgIcon"
                            aria-hidden="true"
                        ></mat-icon>
                    }
                    <span [class.primary-button-text]="data.primaryButton.spinnerOverlay">
                        {{ data.primaryButton.text | translate }}
                    </span>
                    <ng-container
                        [ngTemplateOutlet]="buttonSpinner"
                        [ngTemplateOutletContext]="{ visible: data.primaryButton.spinnerOverlay }"
                    ></ng-container>
                </button>
            }
            @if (data.secondaryButton) {
                <button
                    mat-button
                    class="font-button2"
                    (click)="secondaryButtonClick($event)"
                    [disabled]="data.secondaryButton.isDisabled || data.disabled"
                    [attr.cdkFocusInitial]="!data.primaryButton"
                    [attr.aria-label]="secondaryButtonAriaLabel"
                >
                    @if (data.secondaryButton.svgIcon) {
                        <mat-icon
                            class="trail-button-icon"
                            [class.trail-button-icon-hidden]="data.secondaryButton.spinnerOverlay"
                            [svgIcon]="data.secondaryButton.svgIcon"
                            aria-hidden="true"
                        ></mat-icon>
                    }
                    <span [class.secondary-button-text]="data.secondaryButton.spinnerOverlay">
                        {{ data.secondaryButton.text | translate }}
                    </span>
                    <ng-container
                        [ngTemplateOutlet]="buttonSpinner"
                        [ngTemplateOutletContext]="{ visible: data.secondaryButton.spinnerOverlay }"
                    ></ng-container>
                </button>
            }
        </div>
    }
    @case (trailElementType.Rotary) {
        <div class="trail-container trail-rotary-container">
            <nv-hig-rotary
                [input]="rotaryOptions"
                [initialState]="rotaryInit"
                (selectionChanged)="rotaryChange($event)"
                cdkFocusInitial
                [leftButtonAriaLabel]="data.rotaryLeftAriaLabel"
                [rightButtonAriaLabel]="data.rotaryRightAriaLabel"
                [ariaLabel]="data.rotaryAriaLabel"
                [disabled]="data.disabled"
                [truncateText]="data.truncateRotaryText"
            ></nv-hig-rotary>
        </div>
    }
    @case (trailElementType.Dropdown) {
        <div class="trail-container trail-dropdown-container">
            @if (!openDropdownAsPopup) {
                <mat-form-field class="trail-dropdown-form-field">
                    <mat-select
                        color="accent"
                        class="trail-dropdown hig-button-overlay mat-select-font-body2"
                        panelClass="mat-select-dark-background mat-select-font-body2"
                        [(ngModel)]="selection"
                        placeholder="{{ selection.label }}"
                        (selectionChange)="selectionChange($event)"
                        [disabled]="data.disabled"
                        [attr.disabled]="data.disabled ? true : null"
                        [aria-label]="ariaLabel"
                        nvMatSelectKeyboardFixup
                        cdkMonitorElementFocus
                        cdkFocusInitial
                    >
                        @for (item of data.listItems; track $index) {
                            <mat-option
                                [value]="item"
                                [disabled]="item.disabled"
                                class="font-body2 font-color-tertiary"
                            >
                                {{ item.label | translate }}
                            </mat-option>
                            @if (item.divider) {
                                <mat-divider></mat-divider>
                            }
                        }
                    </mat-select>
                </mat-form-field>
            } @else {
                <mat-form-field>
                    <mat-select
                        color="accent"
                        class="trail-dropdown hig-button-overlay mat-select-font-body2"
                        placeholder="{{ selection.label }}"
                        (click)="selectionChange()"
                        [disabled]="data.disabled"
                        [attr.disabled]="data.disabled ? true : null"
                        nvMatSelectKeyboardFixup
                        nvAddKeyboardActivate
                        cdkMonitorElementFocus
                        cdkFocusInitial
                    ></mat-select>
                </mat-form-field>
            }
        </div>
    }
    @case (trailElementType.PlainText) {
        <div class="trail-container trail-plaintext-container">
            <div
                dir="auto"
                class="font-body2 font-color-secondary"
                [class.disabled-opacity]="data.disabled"
                [innerHtml]="
                    data.plaintext.translated
                        ? data.plaintext.text
                        : (data.plaintext.text | translate: data.plaintext.placeholders)
                "
            ></div>
        </div>
    }
}
<!-- Button spinner -->
<ng-template #buttonSpinner let-visible="visible">
    @if (visible) {
        <div class="trail-button-spinner-overlay">
            <mat-progress-spinner
                class="mat-accent"
                mode="indeterminate"
                [diameter]="spinnerDiameter"
                aria-hidden="true"
            ></mat-progress-spinner>
        </div>
    }
</ng-template>
